import React from 'react';
import useAuth from '../hooks/useAuth';
import API, { AdminAccessProfile } from '../services/API';

type ContextValue = {
  profile: AdminAccessProfile | null;
  loading: boolean;
  can: (module: string, level?: 'read' | 'write') => boolean;
  refresh: () => Promise<void>;
};
const Context = React.createContext<ContextValue | undefined>(undefined);

export function AdminAccessProvider({
  children,
}: React.PropsWithChildren<unknown>) {
  const auth = useAuth();
  const [profile, setProfile] = React.useState<AdminAccessProfile | null>(null);
  const [loading, setLoading] = React.useState(false);
  const refresh = React.useCallback(async () => {
    if (!auth.token) {
      setProfile(null);
      return;
    }
    setLoading(true);
    try {
      setProfile(await API.getAdminProfile(auth.token));
    } finally {
      setLoading(false);
    }
  }, [auth.token]);
  React.useEffect(() => {
    void refresh();
  }, [refresh]);
  const can = React.useCallback(
    (module: string, level: 'read' | 'write' = 'read') => {
      if (!profile?.active) return false;
      if (profile.role === 'superadmin') return true;
      const granted = profile.permissions[module];
      return granted === 'write' || (level === 'read' && granted === 'read');
    },
    [profile],
  );
  return (
    <Context.Provider value={{ profile, loading, can, refresh }}>
      {children}
    </Context.Provider>
  );
}

export default function useAdminAccess() {
  const value = React.useContext(Context);
  if (!value)
    throw new Error('useAdminAccess must be used inside AdminAccessProvider');
  return value;
}
